import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact({ config }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", restaurant: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        apps: config.apps,
        hours_per_day: config.hoursPerDay,
        days_per_week: config.daysPerWeek,
        billing: config.billing,
        estimate: config.estimate || 0,
        language: lang,
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", restaurant: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { k: "name", type: "text", required: true },
    { k: "email", type: "email", required: true },
    { k: "phone", type: "tel", required: false },
    { k: "restaurant", type: "text", required: false },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-36 border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 aurora opacity-60 -z-10" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <Reveal>
          <div className="lg:sticky lg:top-28">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-coral-brand mb-5">{t.contact.overline}</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-white leading-[0.95]">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-white/65 text-lg font-light max-w-md">{t.contact.sub}</p>
            {config.estimate > 0 && (
              <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-cyan-brand/30 bg-cyan-brand/10 px-5 py-3" data-testid="contact-estimate-badge">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-brand">{t.contact.estimateNote}</span>
                <span className="font-display text-xl font-bold text-white">
                  €{config.estimate.toLocaleString(lang === "de" ? "de-DE" : "it-IT", { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="lead-form" className="rounded-3xl border border-white/10 bg-ink-surface/60 backdrop-blur-xl p-7 md:p-9 space-y-5">
            {fields.map((f) => (
              <div key={f.k}>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2">
                  {t.contact[f.k]}{f.required && <span className="text-coral-brand"> *</span>}
                </label>
                <input
                  type={f.type}
                  required={f.required}
                  value={form[f.k]}
                  onChange={set(f.k)}
                  data-testid={`lead-${f.k}`}
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/30 focus:border-cyan-brand focus:outline-none transition-colors duration-200"
                />
              </div>
            ))}
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2">{t.contact.message}</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={set("message")}
                data-testid="lead-message"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/30 focus:border-cyan-brand focus:outline-none transition-colors duration-200 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="lead-submit"
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-coral-brand text-white font-semibold py-3.5 hover:bg-coral-soft transition-colors duration-200 active:scale-95 disabled:opacity-60"
            >
              {loading ? t.contact.sending : t.contact.submit}
              {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
